import { createContext, useContext, useEffect, useState, ReactNode } from "react";

export type Lang = "de" | "en";

interface Ctx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string) => string;
}

const dict: Record<Lang, Record<string, string>> = {
  de: {
    "nav.home": "Start",
    "nav.services": "Leistungen",
    "nav.bezirke": "Bezirke",
    "nav.probleme": "Notfälle",
    "nav.ratgeber": "Ratgeber",
    "nav.preise": "Preise",
    "nav.about": "Über uns",
    "nav.contact": "Kontakt",
    "cta.callNow": "Jetzt anrufen",
    "cta.freeQuote": "Kostenlos anfragen",
    "hero.badge": "24h Notdienst – in 15 Minuten vor Ort",
    "hero.title1": "Schlüsseldienst Wien",
    "hero.title2": "24h Notdienst",
    "hero.subtitle": "Festpreis ab 79 € · Schadenfreie Türöffnung · Geprüfte Techniker · In allen 23 Bezirken",
    "hero.trust1": "Geprüft & versichert",
    "hero.trust2": "15 Min. Anfahrt",
    "hero.trust3": "Festpreis-Garantie",
    "section.services.kicker": "Unsere Leistungen",
    "section.services.title": "Alles aus einer Hand",
    "section.services.subtitle": "Vom Notdienst bis zum Hochsicherheits-Schloss – wir sind Ihr Partner für Sicherheit in Wien.",
    "section.bezirke.kicker": "23 Bezirke",
    "section.bezirke.title": "Schnell in jedem Wiener Bezirk",
    "section.bezirke.subtitle": "Mobile Teams in ganz Wien – wählen Sie Ihren Bezirk.",
    "section.why.kicker": "Warum wir",
    "section.why.title": "Fair, schnell und transparent",
    "section.why.subtitle": "Tausende Wienerinnen und Wiener vertrauen uns – aus gutem Grund.",
    "section.faq.title": "Häufige Fragen",
    "section.contact.title": "Brauchen Sie jetzt Hilfe?",
    "section.contact.subtitle": "Rufen Sie an oder hinterlassen Sie eine Nachricht – wir melden uns binnen 5 Minuten.",
    "form.name": "Name",
    "form.phone": "Telefonnummer",
    "form.address": "Adresse / Bezirk",
    "form.message": "Was ist passiert?",
    "form.submit": "Anfrage senden",
    "form.sent": "Anfrage erhalten – wir rufen Sie zurück.",
    "footer.tagline": "Ihr 24h Schlüsseldienst in Wien – fair, schnell und transparent.",
    "footer.services": "Leistungen",
    "footer.bezirke": "Bezirke",
    "footer.contact": "Kontakt",
    "footer.legal": "Impressum",
    "common.priceFrom": "ab",
    "common.arrival": "Anfahrt",
    "common.readMore": "Mehr erfahren",
    "bezirk.heroTitle": "Schlüsseldienst",
    "bezirk.servicesHere": "Unsere Leistungen in",
    "service.bookNow": "Jetzt buchen",
    "service.faqTitle": "Häufige Fragen",
    "service.areasTitle": "Verfügbar in allen 23 Bezirken",
  },
  en: {
    "nav.home": "Home",
    "nav.services": "Services",
    "nav.bezirke": "Districts",
    "nav.probleme": "Emergencies",
    "nav.ratgeber": "Guide",
    "nav.preise": "Pricing",
    "nav.about": "About",
    "nav.contact": "Contact",
    "cta.callNow": "Call now",
    "cta.freeQuote": "Free quote",
    "hero.badge": "24/7 Emergency – on site in 15 minutes",
    "hero.title1": "Locksmith Vienna",
    "hero.title2": "24/7 Emergency",
    "hero.subtitle": "Fixed price from €79 · Damage-free door opening · Certified technicians · All 23 districts",
    "hero.trust1": "Certified & insured",
    "hero.trust2": "15 min arrival",
    "hero.trust3": "Fixed-price guarantee",
    "section.services.kicker": "Our Services",
    "section.services.title": "Everything from one team",
    "section.services.subtitle": "From emergency unlocking to high-security locks – your partner for safety in Vienna.",
    "section.bezirke.kicker": "23 Districts",
    "section.bezirke.title": "Fast in every Vienna district",
    "section.bezirke.subtitle": "Mobile teams across Vienna – pick your district.",
    "section.why.kicker": "Why us",
    "section.why.title": "Fair, fast and transparent",
    "section.why.subtitle": "Thousands of Viennese trust us – for good reason.",
    "section.faq.title": "Frequently asked questions",
    "section.contact.title": "Need help right now?",
    "section.contact.subtitle": "Call us or leave a message – we get back to you within 5 minutes.",
    "form.name": "Name",
    "form.phone": "Phone number",
    "form.address": "Address / District",
    "form.message": "What happened?",
    "form.submit": "Send request",
    "form.sent": "Request received – we'll call you back.",
    "footer.tagline": "Your 24/7 locksmith in Vienna – fair, fast and transparent.",
    "footer.services": "Services",
    "footer.bezirke": "Districts",
    "footer.contact": "Contact",
    "footer.legal": "Imprint",
    "common.priceFrom": "from",
    "common.arrival": "Arrival",
    "common.readMore": "Learn more",
    "bezirk.heroTitle": "Locksmith",
    "bezirk.servicesHere": "Our services in",
    "service.bookNow": "Book now",
    "service.faqTitle": "Frequently asked questions",
    "service.areasTitle": "Available in all 23 districts",
  },
};

const defaultCtx: Ctx = {
  lang: "de",
  setLang: () => {},
  t: (key: string) => dict.de[key] ?? key,
};

const LanguageContext = createContext<Ctx>(defaultCtx);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(() => {
    if (typeof window === "undefined") return "de";
    const stored = localStorage.getItem("lang") as Lang | null;
    return stored ?? "de";
  });

  useEffect(() => {
    document.documentElement.lang = lang;
    localStorage.setItem("lang", lang);
  }, [lang]);

  const t = (key: string) => dict[lang][key] ?? key;

  return (
    <LanguageContext.Provider value={{ lang, setLang: setLangState, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  return ctx;
};